import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { Loader2 } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

export default function SalaryReportSection() {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)
  );
  const [toDate, setToDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10)
  );
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const { data: rows, isLoading, error } = useQuery({
    queryKey: ['salaryReport', fromDate, toDate],
    queryFn: async () => {
      const res = await posApi.getSalaryReport({ from_date: fromDate, to_date: toDate });
      return res.data.message as any[];
    },
  });

  const selectedData = useMemo(() => {
    if (!selectedEmployee || !rows) return null;
    return rows.find((r: any) => r.user === selectedEmployee);
  }, [rows, selectedEmployee]);

  if (error) {
    return <div className="text-red-500">Failed to load report.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.fromDate')}</label>
          <input
            type="date"
            className="input-field"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.toDate')}</label>
          <input
            type="date"
            className="input-field"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          <select
            className="input-field min-w-[200px]"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value="">All Employees</option>
            {rows?.map((r: any) => (
              <option key={r.user} value={r.user}>
                {r.full_name || r.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          {selectedEmployee && selectedData ? (
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
                <tr>
                  <th className="px-6 py-4 font-medium">Date</th>
                  <th className="px-6 py-4 font-medium">Expense ID</th>
                  <th className="px-6 py-4 font-medium">Remarks</th>
                  <th className="px-6 py-4 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {selectedData.expenses?.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      No salaries/expenses recorded for this employee.
                    </td>
                  </tr>
                ) : (
                  selectedData.expenses?.map((exp: any) => (
                    <tr key={exp.name} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">{format(new Date(exp.posting_date), 'MMM d, yyyy')}</td>
                      <td className="px-6 py-4 text-blue-600 font-medium">{exp.name}</td>
                      <td className="px-6 py-4">{exp.remarks || '-'}</td>
                      <td className="px-6 py-4 text-right font-medium text-red-600">
                        {formatMoney(exp.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {selectedData.expenses && selectedData.expenses.length > 0 && (
                <tfoot className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900">
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-right">Total:</td>
                    <td className="px-6 py-4 text-right text-red-600">
                      {formatMoney(selectedData.expenses.reduce((sum: number, exp: any) => sum + (exp.amount || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
                <tr>
                  <th className="px-6 py-4 font-medium">Employee Name</th>
                  <th className="px-6 py-4 font-medium">Role</th>
                  <th className="px-6 py-4 font-medium text-right">Fixed Salary</th>
                  <th className="px-6 py-4 font-medium text-right">Paid Expenses</th>
                  <th className="px-6 py-4 font-medium text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading report...
                    </td>
                  </tr>
                ) : rows?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No records found for this period.
                    </td>
                  </tr>
                ) : (
                  rows?.map((r: any) => (
                    <tr key={r.name} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{r.full_name || r.name}</div>
                        {r.user && <div className="text-xs text-gray-500">{r.user}</div>}
                      </td>
                      <td className="px-6 py-4">{r.role}</td>
                      <td className="px-6 py-4 text-right font-medium">{formatMoney(r.salary || 0)}</td>
                      <td className="px-6 py-4 text-right">
                        {r.paid_amount > 0 ? (
                          <span className="text-red-600">{formatMoney(r.paid_amount)}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        <span className={r.balance < 0 ? 'text-red-600' : 'text-emerald-600'}>
                          {formatMoney(r.balance || 0)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows && rows.length > 0 && (
                <tfoot className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900">
                  <tr>
                    <td colSpan={2} className="px-6 py-4 text-right">Total:</td>
                    <td className="px-6 py-4 text-right">
                      {formatMoney(rows.reduce((sum: number, r: any) => sum + (r.salary || 0), 0))}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-red-600">
                        {formatMoney(rows.reduce((sum: number, r: any) => sum + (r.paid_amount || 0), 0))}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {(() => {
                        const totalBalance = rows.reduce((sum: number, r: any) => sum + (r.balance || 0), 0);
                        return (
                          <span className={totalBalance < 0 ? 'text-red-600' : 'text-emerald-600'}>
                            {formatMoney(totalBalance)}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
